import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchServiceProfile, updateServiceProfile, fetchDesignSystemTemplate } from '@/services/serviceProfiles';
import { VisualTokenEditor } from '@/components/design-system/VisualTokenEditor';

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: 'none',
};

export function DesignSystemEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['serviceProfile', id],
    queryFn: () => fetchServiceProfile(id!),
    enabled: !!id,
  });

  const [markdown, setMarkdown] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [activeTab, setActiveTab] = useState<'visual' | 'text'>('visual');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Initialize markdown from profile
  useEffect(() => {
    if (profile && !isDirty) {
      setMarkdown(profile.designSystemMd || '');
    }
  }, [profile, isDirty]);

  const saveMutation = useMutation({
    mutationFn: () => updateServiceProfile(id!, { designSystemMd: markdown }),
    onSuccess: () => {
      setIsDirty(false);
      setSaveMessage({ text: 'Saved', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['serviceProfile', id] });
      queryClient.invalidateQueries({ queryKey: ['serviceProfiles'] });
      setTimeout(() => setSaveMessage(null), 2000);
    },
    onError: (err: Error) => {
      setSaveMessage({ text: err.message, type: 'error' });
    },
  });

  const handleMarkdownChange = (md: string) => {
    setMarkdown(md);
    setIsDirty(true);
  };

  const handleDiscard = () => {
    setMarkdown(profile?.designSystemMd || '');
    setIsDirty(false);
  };

  const handleImportTemplate = async () => {
    setLoadingTemplate(true);
    try {
      const { content } = await fetchDesignSystemTemplate();
      setMarkdown(content);
      setIsDirty(true);
    } catch {
      setSaveMessage({ text: 'Failed to load template', type: 'error' });
    } finally {
      setLoadingTemplate(false);
    }
  };

  if (isLoading) return <p style={{ color: 'var(--text-muted)' }}>Loading...</p>;
  if (!profile) return <p style={{ color: 'var(--color-error)' }}>Service profile not found.</p>;

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    backgroundColor: 'transparent',
    color: active ? '#2563eb' : 'var(--text-muted)',
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <button
            onClick={() => navigate(`/ui-orchestrator/services/${id}`)}
            style={{ ...buttonStyle, backgroundColor: 'transparent', color: 'var(--color-primary)', padding: 0, marginBottom: '6px', fontSize: '12px' }}
          >
            &larr; Back to Service
          </button>
          <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>
            Design System — {profile.displayName}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {saveMessage && (
            <span style={{
              fontSize: '12px',
              fontWeight: 600,
              color: saveMessage.type === 'success' ? '#166534' : '#dc2626',
            }}>
              {saveMessage.text}
            </span>
          )}
          {isDirty && (
            <button onClick={handleDiscard}
              style={{ ...buttonStyle, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: '12px', padding: '6px 12px' }}>
              Discard
            </button>
          )}
          <button
            onClick={handleImportTemplate}
            disabled={loadingTemplate}
            style={{ ...buttonStyle, backgroundColor: '#f0f9ff', color: '#0369a1', fontSize: '12px', padding: '6px 12px' }}
          >
            {loadingTemplate ? 'Loading...' : 'Import Default'}
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending}
            style={{
              ...buttonStyle,
              backgroundColor: !isDirty ? '#93c5fd' : '#2563eb',
              color: '#fff',
              fontSize: '12px',
              padding: '6px 16px',
              opacity: !isDirty ? 0.6 : 1,
            }}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '16px' }}>
        <button style={tabStyle(activeTab === 'visual')} onClick={() => setActiveTab('visual')}>Visual</button>
        <button style={tabStyle(activeTab === 'text')} onClick={() => setActiveTab('text')}>Text</button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', alignSelf: 'center' }}>
          {markdown ? `${markdown.split('\n').length} lines` : 'Empty'}
        </span>
      </div>

      {/* Tab content */}
      {activeTab === 'visual' ? (
        markdown.trim() ? (
          <VisualTokenEditor markdown={markdown} onMarkdownChange={handleMarkdownChange} />
        ) : (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
            <p style={{ fontSize: '14px', marginBottom: '12px' }}>No design system markdown yet.</p>
            <button onClick={handleImportTemplate} disabled={loadingTemplate}
              style={{ ...buttonStyle, backgroundColor: '#2563eb', color: '#fff' }}>
              {loadingTemplate ? 'Loading...' : 'Import Default Template'}
            </button>
          </div>
        )
      ) : (
        <textarea
          style={{
            width: '100%',
            minHeight: '600px',
            padding: '12px',
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: 1.5,
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
          value={markdown}
          onChange={(e) => handleMarkdownChange(e.target.value)}
          placeholder="Paste or write your design system markdown here..."
        />
      )}
    </div>
  );
}
